




















import { SearchDistributionData } from '@empathybroker/tsclient-stats/dist/models/distributionreport/searchdistributiondata';
import { SearchDistributionDataPeriod } from '@empathybroker/tsclient-stats/dist/models/distributionreport/searchdistributiondataperiod';

import { mapGetters } from 'vuex';

import { CompareFilterEnum, FilterIdEnum, KpiEnum } from '../../enums';

import { PeriodsData, RegularFilter } from '../../models';

import { ANALYTICS_CONSTS } from '../../store/analytics/analytics.consts';
import { CONFIG_CONSTS } from '../../store/config/config.consts';
import { CONTEXT_CONSTS } from '../../store/context/context.consts';

import { UiSwitch } from '../keen-ui';

import AreaChart from './area-chart.component.vue';
import KpisLegend from './kpis-legend.component.vue';

export default {
  name: 'distribution-chart',
  components: {
    UiSwitch,
    KpisLegend,
    AreaChart
  },
  props: {
    selectedId: String
  },
  computed: {
    ...mapGetters(CONFIG_CONSTS.moduleName, {
      kpis: CONFIG_CONSTS.getters.kpis
    }),
    ...mapGetters(CONTEXT_CONSTS.moduleName, {
      filters: CONTEXT_CONSTS.getters.enabledFilters
    }),
    ...mapGetters(ANALYTICS_CONSTS.moduleName, {
      data: ANALYTICS_CONSTS.getters.data
    }),
    supportedKpis: function (): KpiEnum[] {
      const supportedKpis = [
        KpiEnum.QUERIES,
        KpiEnum.CLICKS,
        KpiEnum.FINDABILITY,
        KpiEnum.ADD2CART,
        KpiEnum.CHECKOUT,
        KpiEnum.NO_RESULTS
      ];
      return this.kpis.filter((kpi: KpiEnum) => supportedKpis.includes(kpi));
    },
    kpisLegendItems: function (): any[] {
      const kpisMessages = this.messages.kpis;
      return this.supportedKpis.reduce((kpisLegendItems: any[], kpi: KpiEnum) => {
        if (!this.showPercentage || (kpi !== KpiEnum.QUERIES)) {
          const kpiText = {
            [KpiEnum.QUERIES]: () => kpisMessages.queries.valueLabel,
            [KpiEnum.CLICKS]: () => (!this.showPercentage ? kpisMessages.clicks.valueLabel : kpisMessages.clicks.percentageLabel),
            [KpiEnum.FINDABILITY]: () => (!this.showPercentage ? kpisMessages.findability.valueLabel
              : kpisMessages.findability.percentageLabel),
            [KpiEnum.ADD2CART]: () => (!this.showPercentage ? kpisMessages.add2cart.valueLabel : kpisMessages.add2cart.percentageLabel),
            [KpiEnum.CHECKOUT]: () => (!this.showPercentage ? kpisMessages.checkout.valueLabel : kpisMessages.checkout.percentageLabel),
            [KpiEnum.NO_RESULTS]: () => kpisMessages.noResults.valueLabel
          }[kpi]();
          kpisLegendItems.push({
            id: kpi,
            color: kpi,
            text: kpiText.toUpperCase()
          });
        }
        return kpisLegendItems;
      }, []);
    },
    compareFilterValue: function (): CompareFilterEnum {
      const compareFilter: RegularFilter = this.filters[FilterIdEnum.COMPARE];
      if (!!compareFilter) {
        return (compareFilter.selectedValues[0] as CompareFilterEnum);
      }
    },
    areaChartParams: function () {
      return {
        config: {
          values: this.selectedKpis,
          maxValue: (this.showPercentage ? 100 : undefined),
          valueUnits: (this.showPercentage ? '%' : undefined)
        },
        data: this.areaChartData
      };
    },
    areaChartData: function (): any[] {
      let distData: PeriodsData;
      if (!this.selectedId) {
        distData = this.data.overall.dist;
      } else {
        const selectedIdData = this.data.ids[this.selectedId];
        if (!!selectedIdData) {
          distData = selectedIdData.dist;
        }
      }
      let data = [];
      if (!!distData && !!distData.currentPeriod) {
        const distributionData = distData.currentPeriod.data.distributionData;
        let distributionDataToCompare: SearchDistributionData;
        if (!!this.compareFilterValue && !!distData[this.compareFilterValue]) {
          distributionDataToCompare = distData[this.compareFilterValue].data.distributionData;
        }
        let isHourlyDist = false;
        if (distributionData.length > 1) {
          isHourlyDist = ((distributionData[1].datetime.getTime() - distributionData[0].datetime.getTime()) < (((1000 * 60) * 60) * 12));
        }
        data = distributionData.map((distributionDataItem: SearchDistributionDataPeriod, index: number) => {
          const distributionDataItemToCompare = (!!distributionDataToCompare ? distributionDataToCompare[index] : undefined);
          return this.getAreaChartDataItem(distributionDataItem, distributionDataItemToCompare, isHourlyDist);
        });
      }
      return data;
    }
  },
  data: function () {
    return {
      showPercentage: false,
      selectedKpis: [],
      messages: this.$epb.messages.analytics
    };
  },
  methods: {
    getInitialSelectedKpis: function (): KpiEnum[] {
      let selectedKpis = this.kpisLegendItems.map((kpiLegendItem) => kpiLegendItem.id);
      if (!!this.compareFilterValue) {
        selectedKpis = selectedKpis.slice(0, 1);
      }
      return selectedKpis;
    },
    getAreaChartDataItem: function (distributionDataItem: SearchDistributionDataPeriod,
      distributionDataItemToCompare?: SearchDistributionDataPeriod, isHourlyDist = false) {
      return {
        id: distributionDataItem.datetime.toISOString(),
        labels: this.getAreaChartDataItemLabels(distributionDataItem, distributionDataItemToCompare, isHourlyDist),
        values: this.getAreaChartDataItemValues(distributionDataItem, distributionDataItemToCompare)
      };
    },
    getAreaChartDataItemLabels: function (distributionDataItem: SearchDistributionDataPeriod,
      distributionDataItemToCompare: SearchDistributionDataPeriod, isHourlyDist: boolean): string[] {
      const dateLocaleOptions = (isHourlyDist ? {
        hour: '2-digit',
        minute: '2-digit'
      } : {
        month: 'short',
        day: 'numeric'
      });
      const dateWithoutOffset = this.getDateWithoutTimezone(distributionDataItem.datetime);
      const labels = [dateWithoutOffset.toLocaleString(this.$epb.locale, dateLocaleOptions)];
      if (!!distributionDataItemToCompare) {
        const dateToCompareWithoutOffset = this.getDateWithoutTimezone(distributionDataItemToCompare.datetime);
        labels.push(`${ dateToCompareWithoutOffset.toLocaleString(this.$epb.locale, dateLocaleOptions) } ${ this.compareFilterValue }`);
      }
      return labels;
    },
    getDateWithoutTimezone: function (date: Date): Date {
      return new Date(date.getTime() + (date.getTimezoneOffset() * (60 * 1000)));
    },
    getAreaChartDataItemValues: function (distributionDataItem: SearchDistributionDataPeriod,
      distributionDataItemToCompare: SearchDistributionDataPeriod) {
      const values = {};
      this.supportedKpis.forEach((kpi: KpiEnum) => {
        values[kpi] = {
          [KpiEnum.QUERIES]: () => {
            const kpiValueId = this.getKpiValueId(kpi);
            const kpiValues = [distributionDataItem[kpiValueId]];
            if (!!distributionDataItemToCompare) {
              kpiValues.push(distributionDataItemToCompare[kpiValueId]);
            }
            return kpiValues;
          },
          [KpiEnum.CLICKS]: () => {
            const kpiValueId = this.getKpiValueId(kpi);
            const kpiValues = [distributionDataItem[kpiValueId]];
            if (!!distributionDataItemToCompare) {
              kpiValues.push(distributionDataItemToCompare[kpiValueId]);
            }
            return kpiValues;
          },
          [KpiEnum.FINDABILITY]: () => {
            const kpiValueId = this.getKpiValueId(kpi);
            const kpiValues = [distributionDataItem[kpiValueId]];
            if (!!distributionDataItemToCompare) {
              kpiValues.push(distributionDataItemToCompare[kpiValueId]);
            }
            return kpiValues;
          },
          [KpiEnum.ADD2CART]: () => {
            const kpiValueId = this.getKpiValueId(kpi);
            const kpiValues = [distributionDataItem[kpiValueId]];
            if (!!distributionDataItemToCompare) {
              kpiValues.push(distributionDataItemToCompare[kpiValueId]);
            }
            return kpiValues;
          },
          [KpiEnum.CHECKOUT]: () => {
            const kpiValueId = this.getKpiValueId(kpi);
            const kpiValues = [distributionDataItem[kpiValueId]];
            if (!!distributionDataItemToCompare) {
              kpiValues.push(distributionDataItemToCompare[kpiValueId]);
            }
            return kpiValues;
          },
          [KpiEnum.NO_RESULTS]: () => {
            const kpiValueId = this.getKpiValueId(kpi);
            const kpiValues = [distributionDataItem[kpiValueId]];
            if (!!distributionDataItemToCompare) {
              kpiValues.push(distributionDataItemToCompare[kpiValueId]);
            }
            return kpiValues;
          }
        }[kpi]();
      });
      return values;
    },
    getKpiValueId: function (kpi: KpiEnum): string {
      return {
        [KpiEnum.QUERIES]: () => (this.showPercentage ? 'queryPct' : 'queryCount'),
        [KpiEnum.CLICKS]: () => (this.showPercentage ? 'clickPct' : 'clickCount'),
        [KpiEnum.FINDABILITY]: () => (this.showPercentage ? 'findabilityPct' : 'findabilityCount'),
        [KpiEnum.ADD2CART]: () => (this.showPercentage ? 'add2cartPct' : 'add2cartCount'),
        [KpiEnum.CHECKOUT]: () => (this.showPercentage ? 'checkoutPct' : 'checkoutCount'),
        [KpiEnum.NO_RESULTS]: () => (this.showPercentage ? 'noResultsPct' : 'noResultsCount')
      }[kpi]();
    }
  },
  mounted: function (): void {
    this.selectedKpis = this.getInitialSelectedKpis();
  },
  watch: {
    showPercentage: function (showPercentage: boolean): void {
      if (showPercentage) {
        const indexOfQueries = this.selectedKpis.indexOf(KpiEnum.QUERIES);
        if (indexOfQueries >= 0) {
          if (this.selectedKpis.length > 1) {
            this.selectedKpis.splice(indexOfQueries, 1);
          } else {
            this.selectedKpis = this.getInitialSelectedKpis();
          }
        }
      }
    },
    compareFilterValue: function (compareFilterValue: CompareFilterEnum): void {
      if (!!compareFilterValue) {
        this.selectedKpis = this.selectedKpis.slice(0, 1);
      }
    }
  }
};

